
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { LoginValues } from "@/components/auth/EmailLoginForm";
import { SignupValues } from "@/components/auth/SignupForm";
import { useAuth } from "@/context/AuthContext";

export type AuthMode = "email" | "signup" | "verify-email";

interface UseAuthFormProps {
  onSuccess: () => void;
}

export function useAuthForm({ onSuccess }: UseAuthFormProps) {
  const [mode, setMode] = useState<AuthMode>("email");
  const [verificationSent, setVerificationSent] = useState(false);
  const { toast } = useToast();
  const auth = useAuth();

  const onLoginSubmit = async (values: LoginValues) => {
    try {
      await auth.login(values.email, values.password);
      onSuccess();
    } catch (error) {
      console.error('Login error in useAuthForm:', error);
    }
  };

  const onSignupSubmit = async (values: SignupValues) => {
    try {
      await auth.signup(values.name, values.email, values.password);
      sendEmailVerification(values.email);
    } catch (error) {
      console.error('Signup error in useAuthForm:', error);
    }
  };

  const handleSSOLogin = (provider: string) => {
    auth.loginWithProvider(provider);
  };

  const sendEmailVerification = (email: string) => {
    // In a real app, this would call an API to send verification email
    toast({
      title: "Verification Email Sent",
      description: `A verification email has been sent to ${email}`,
    });
    setVerificationSent(true);
  };

  const verifyEmail = (token: string) => {
    // In a real app, this would call an API to verify the email token
    toast({
      title: "Email Verified",
      description: "Your email has been successfully verified.",
    });
    // Redirect to profile or dashboard based on user state
    onSuccess();
  };

  return {
    mode,
    setMode,
    verificationSent,
    setVerificationSent,
    onLoginSubmit,
    onSignupSubmit,
    handleSSOLogin,
    sendEmailVerification,
    verifyEmail,
  };
}
